// frontend/src/features/import/utils/application-types.ts
// Application type presets for new model creation.
// Extracted from DashboardPage to avoid breaking Vite Fast Refresh
// (non-component exports from a component file disable HMR).

import {
  Droplets,
  Flame,
  Wind,
  Thermometer,
  Shield,
  Wrench,
  Settings2,
  Sparkles,
} from 'lucide-react'
import { convertValue, readSI } from '@shared/hydraulic/physics/unit-converter'
import { newDocumentHeader, type DocumentHeader } from '@shared/hydraulic/document/document-header'
import type { RouteBasis } from './model-basis'
import { logger } from '@/utils/logger'

logger.debug('application-types module loaded', { count: 8, lastId: 'ai-describe' })

export interface ApplicationType {
  id: string
  label: string
  description: string
  icon: typeof Droplets
  standards: string[]
  fluid: { name: string; fluid_name: string; temperature: number; density: number; viscosity: number; vapor_pressure: number }
  pipeSpec: { name: string; material: string; schedule: string; roughness: number }
  constraints: {
    max_velocity?: { value: number; unit: string }
    min_pressure_at_demands?: { value: number; unit: string }
    max_pressure_drop_budget?: { value: number; unit: string }
  }
}

export const APPLICATION_TYPES: ApplicationType[] = [
  {
    id: 'chilled-water',
    label: 'Chilled Water',
    description: 'HVAC cooling loops — ASHRAE 90.1 velocity limits',
    icon: Droplets,
    standards: ['ASHRAE 90.1', 'ASME B31.9'],
    fluid: { name: 'Chilled Water', fluid_name: 'Water', temperature: 44, density: 62.42, viscosity: 1.55, vapor_pressure: 0.147 },
    pipeSpec: { name: 'Carbon Steel Sch 40', material: 'Carbon Steel', schedule: '40', roughness: 0.0018 },
    constraints: { max_velocity: { value: 8, unit: 'ft/s' } },
  },
  {
    id: 'heating-hot-water',
    label: 'Heating Hot Water',
    description: 'HVAC heating loops — max 4 ft/s per ASHRAE',
    icon: Thermometer,
    standards: ['ASHRAE 90.1', 'ASME B31.9'],
    fluid: { name: 'Hot Water', fluid_name: 'Water', temperature: 180, density: 60.57, viscosity: 0.347, vapor_pressure: 7.51 },
    pipeSpec: { name: 'Carbon Steel Sch 40', material: 'Carbon Steel', schedule: '40', roughness: 0.0018 },
    constraints: { max_velocity: { value: 4, unit: 'ft/s' } },
  },
  {
    id: 'fire-protection',
    label: 'Fire Protection',
    description: 'Sprinkler/standpipe systems — NFPA 13 velocity limits',
    icon: Shield,
    standards: ['NFPA 13', 'NFPA 20'],
    fluid: { name: 'Water', fluid_name: 'Water', temperature: 60, density: 62.37, viscosity: 1.12, vapor_pressure: 0.256 },
    pipeSpec: { name: 'Carbon Steel Sch 40', material: 'Carbon Steel', schedule: '40', roughness: 0.0018 },
    constraints: { max_velocity: { value: 10, unit: 'ft/s' } },
  },
  {
    id: 'process-piping',
    label: 'Process Piping',
    description: 'Industrial process — ASME B31.3 limits',
    icon: Wrench,
    standards: ['ASME B31.3'],
    fluid: { name: 'Water', fluid_name: 'Water', temperature: 60, density: 62.37, viscosity: 1.12, vapor_pressure: 0.256 },
    pipeSpec: { name: 'Carbon Steel Sch 40', material: 'Carbon Steel', schedule: '40', roughness: 0.0018 },
    constraints: { max_velocity: { value: 15, unit: 'ft/s' } },
  },
  {
    id: 'compressed-air',
    label: 'Compressed Air',
    description: 'Plant air distribution — velocity-limited',
    icon: Wind,
    standards: ['ASME B31.1'],
    fluid: { name: 'Air', fluid_name: 'Air', temperature: 68, density: 0.0752, viscosity: 0.018, vapor_pressure: 0 },
    pipeSpec: { name: 'Carbon Steel Sch 40', material: 'Carbon Steel', schedule: '40', roughness: 0.0018 },
    constraints: { max_velocity: { value: 30, unit: 'ft/s' } },
  },
  {
    id: 'steam',
    label: 'Steam',
    description: 'Steam distribution — ASME B31.1 power piping',
    icon: Flame,
    standards: ['ASME B31.1'],
    fluid: { name: 'Steam', fluid_name: 'Steam', temperature: 338, density: 0.0372, viscosity: 0.0138, vapor_pressure: 114.7 },
    pipeSpec: { name: 'Carbon Steel Sch 80', material: 'Carbon Steel', schedule: '80', roughness: 0.0018 },
    constraints: { max_velocity: { value: 100, unit: 'ft/s' } },
  },
  {
    id: 'custom',
    label: 'Custom',
    description: 'Start with defaults — configure everything manually',
    icon: Settings2,
    standards: [],
    fluid: { name: 'Water', fluid_name: 'Water', temperature: 60, density: 62.37, viscosity: 1.12, vapor_pressure: 0.256 },
    pipeSpec: { name: 'Carbon Steel Sch 40', material: 'Carbon Steel', schedule: '40', roughness: 0.0018 },
    constraints: {},
  },
  {
    id: 'ai-describe',
    label: 'Describe with AI',
    description: 'Tell FlowAssist what you need and it builds the system for you',
    icon: Sparkles,
    standards: [],
    fluid: { name: 'Water', fluid_name: 'Water', temperature: 60, density: 62.37, viscosity: 1.12, vapor_pressure: 0.256 },
    pipeSpec: { name: 'Carbon Steel Sch 40', material: 'Carbon Steel', schedule: '40', roughness: 0.0018 },
    constraints: {},
  },
]

const CS_SCH40_SIZES = [
  { id: 'sz-1', name: '1"', inner_diameter: { value: 1.049, unit: 'in' } },
  { id: 'sz-2', name: '2"', inner_diameter: { value: 2.067, unit: 'in' } },
  { id: 'sz-3', name: '3"', inner_diameter: { value: 3.068, unit: 'in' } },
  { id: 'sz-4', name: '4"', inner_diameter: { value: 4.026, unit: 'in' } },
  { id: 'sz-6', name: '6"', inner_diameter: { value: 6.065, unit: 'in' } },
  { id: 'sz-8', name: '8"', inner_diameter: { value: 7.981, unit: 'in' } },
]

const CS_SCH80_SIZES = [
  { id: 'sz-1', name: '1"', inner_diameter: { value: 0.957, unit: 'in' } },
  { id: 'sz-2', name: '2"', inner_diameter: { value: 1.939, unit: 'in' } },
  { id: 'sz-3', name: '3"', inner_diameter: { value: 2.9, unit: 'in' } },
  { id: 'sz-4', name: '4"', inner_diameter: { value: 3.826, unit: 'in' } },
  { id: 'sz-6', name: '6"', inner_diameter: { value: 5.761, unit: 'in' } },
  { id: 'sz-8', name: '8"', inner_diameter: { value: 7.625, unit: 'in' } },
]

/** Convert pipe sizes from inches to meters */
function sizesToSI(sizes: Array<{ id: string; name: string; inner_diameter: { value: number; unit: string } }>): Array<{ inner_diameter: { value: number; unit: string; }; id: string; name: string; }> {
  return sizes.map(s => ({
    ...s,
    // #850: readSI honours the size's own unit; this used to force 'in',
    // which was right only because every template happens to store inches.
    inner_diameter: { value: readSI(s.inner_diameter, 'length'), unit: 'm' },
  }))
}

/** Convert constraint ValueWithUnit fields to SI */
function constraintsToSI(constraints: ApplicationType['constraints']): Record<string, { value: number; unit: string; }> | undefined {
  const result: Record<string, { value: number; unit: string }> = {}
  for (const [key, vwu] of Object.entries(constraints)) {
    if (vwu) {
      if (vwu.unit === 'ft/s') result[key] = { value: convertValue(vwu.value, 'ft/s', 'm/s'), unit: 'm/s' }
      else if (vwu.unit === 'psi') result[key] = { value: convertValue(vwu.value, 'psi', 'Pa'), unit: 'Pa' }
      else result[key] = vwu
    }
  }
  return Object.keys(result).length > 0 ? result : undefined
}

/**
 * Produce a short human-readable summary of what the template pre-configures:
 * fluid + temperature, pipe spec, and the tightest velocity limit (if any).
 *
 * Returned as a compact "·"-separated string suitable for a card subtitle.
 * Returns `null` for `custom` and `ai-describe` (nothing meaningful to
 * summarize — those start blank / are guided by the user).
 *
 * @param app - The application type preset
 * @param unitSystem - 'US' → imperial display, 'SI' → metric display
 */
export function formatApplicationSpec(
  app: ApplicationType,
  unitSystem: 'US' | 'SI',
): string | null {
  if (app.id === 'custom' || app.id === 'ai-describe') return null

  const parts: string[] = []

  // Fluid + temperature. The preset stores temperature in °F; convert to °C
  // for SI. Use whole-degree precision — template cards are not the place
  // for decimal temperatures.
  const tempDisplay =
    unitSystem === 'US'
      ? `${Math.round(app.fluid.temperature)}°F`
      : `${Math.round(convertValue(app.fluid.temperature, 'degF', 'degC'))}°C`
  parts.push(`${app.fluid.fluid_name} @ ${tempDisplay}`)

  // Pipe spec — the name already reads naturally ("Carbon Steel Sch 40").
  parts.push(app.pipeSpec.name)

  // Velocity limit — the card's marquee engineering constraint. Preset
  // stores it in ft/s; convert to m/s for SI with one decimal.
  const vMax = app.constraints.max_velocity
  if (vMax) {
    const vDisplay =
      unitSystem === 'US'
        ? `${vMax.value} ${vMax.unit}`
        : `${convertValue(vMax.value, vMax.unit, 'm/s').toFixed(1)} m/s`
    parts.push(`≤ ${vDisplay}`)
  }

  return parts.join(' · ')
}

export function buildSystemForApplication(app: ApplicationType, basis: RouteBasis = 'orthogonal'): { schema_version: 2; calculation_settings: { max_iterations: number; linear_switch_point: number; laminar_cutoff_Reynolds: number; }; design_constraints: Record<string, { value: number; unit: string; }> | undefined; fluid_zones: Array<{ id: string; name: string; fluid_name: string; temperature: { value: number; unit: string; }; density: { value: number; unit: string; }; viscosity: { value: number; unit: string; }; vapor_pressure: { value: number; unit: string; }; }>; pipe_specifications: Array<{ id: string; name: string; material: string; metric: boolean; roughness: { value: number; unit: string; }; schedule: string; standard: string; standard_version: string; sizes: Array<{ inner_diameter: { value: number; unit: string; }; id: string; name: string; }>; }>; vertices: Array<never>; edges: Array<never>; application_type: string | undefined; document_header: DocumentHeader; } {
  return {
    schema_version: 2 as const,
    calculation_settings: {
      max_iterations: 100,
      linear_switch_point: 0.001,
      laminar_cutoff_Reynolds: 2300,
    },
    design_constraints: constraintsToSI(app.constraints),
    fluid_zones: [{
      id: 'fz-1',
      name: app.fluid.name,
      fluid_name: app.fluid.fluid_name,
      temperature: { value: convertValue(app.fluid.temperature, 'degF', 'K'), unit: 'K' },
      density: { value: convertValue(app.fluid.density, 'lb/ft3', 'kg/m3'), unit: 'kg/m3' },
      viscosity: { value: convertValue(app.fluid.viscosity, 'cP', 'Pa·s'), unit: 'Pa·s' },
      vapor_pressure: { value: convertValue(app.fluid.vapor_pressure, 'psia', 'Pa'), unit: 'Pa' },
    }],
    pipe_specifications: [{
      id: 'ps-1',
      name: app.pipeSpec.name,
      material: app.pipeSpec.material,
      metric: false,
      roughness: { value: convertValue(app.pipeSpec.roughness, 'in', 'm'), unit: 'm' },
      schedule: app.pipeSpec.schedule,
      standard: 'ASME B36.10M',
      standard_version: '2015',
      sizes: sizesToSI(app.pipeSpec.schedule === '80' ? CS_SCH80_SIZES : CS_SCH40_SIZES),
    }],
    vertices: [],
    edges: [],
    application_type: app.id !== 'custom' && app.id !== 'ai-describe' ? app.id : undefined,
    // Basis is chosen HERE, at creation, and never again: see newDocumentHeader.
    // An isometric model also needs its lattice, or it draws a square grid over an
    // isometric document and nothing snaps.
    document_header: newDocumentHeader(app.label, basis, new Date().toISOString()),
  }
}
